import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useUserProfileContext } from '../../../../contexts/UserProfileContext';
import { useToast } from '../../../../contexts/ToastContext';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useTheme } from '../../../../ThemeContext';
import { authService } from '../../../../services/auth.service';
import { useSetupBadge } from '../../../../hooks/useSetupBadge';
import { ProfileType } from '../../../../types/enums';
import LoadingOverlay from '../../../LoadingOverlay/LoadingOverlay';
import ProfileSwitcher from '../../../ProfileSwitcher/ProfileSwitcher';
import StaffMenuIdentity from './StaffMenuIdentity';
import styles from './menu.module.css';

interface MenuComponentProps {
    onSettingsClick?: () => void;
    onFaqClick?: () => void;
}

const Menu: React.FC<MenuComponentProps> = ({
    onSettingsClick = () => { },
    onFaqClick = () => { },
}) => {
    const { clearTokens, userType } = useAuthContext();
    const { profile, loading } = useUserProfileContext();
    const { showToast } = useToast();
    const { language, setLanguage, t } = useLanguage();
    const { theme, setTheme } = useTheme();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // AB#1675 — surface "My Business" with a SET UP highlight while incomplete.
    // O3 (SO-22) — only while the business is not live yet (see useSetupBadge).
    const isIncomplete = useSetupBadge();
    const showMyBusinessLink = userType === ProfileType.SalonOwner;
    // S2 (AB#2205, SG-17) — staff see their role + salon under their name.
    const isStaff = userType === ProfileType.SalonStaff;

    const defaultAvatar = '/assets/icons/avatar.png';

    const handleMyBusinessClick = () => {
        setIsOpen(false);
        navigate('/my-business');
    };

    const handleToggle = () => {
        setIsOpen(!isOpen);
    };

    const handleLogout = async () => {
        try {
            setIsLoggingOut(true);
            const success = await authService.logout();
            if (success) {
                clearTokens();
            } else {
                showToast(t('shell.menu.logoutFailed'), 'error');
            }
        } catch {
            showToast(t('shell.menu.logoutFailed'), 'error');
        } finally {
            setIsLoggingOut(false);
        }
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleEnglish = () => {
        setLanguage('en');
        showToast(t('toast.languageSet'), 'info');
    };

    const handleArabic = () => {
        setLanguage('ar');
        showToast(t('toast.languageSetAr'), 'info');
    };

    const handleLightTheme = () => {
        setTheme('light');
        showToast(t('toast.themeSetLight'), 'info');
    };

    const handleDarkTheme = () => {
        setTheme('dark');
        showToast(t('toast.themeSetDark'), 'info');
    };

    return (
        <>
            {/* O1 (AB#2199, SO-43) — the profile load no longer blocks the whole portal
                behind a full-screen "Loading user info…" modal on every page load; the
                menu just shows its avatar until the name arrives. Logging out still
                blocks (nothing else may run while the session ends). */}
            {isLoggingOut && (
                <LoadingOverlay message={t('shell.menu.loggingOut')} />
            )}
            <div className={styles.menuContainer} ref={menuRef}>
                <button
                    className={`${styles.menuTrigger} arrowUpDown ${isOpen ? 'isOpen' : ''}`}
                    onClick={handleToggle}
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    aria-label={`${t('shell.menu.profileMenu')}${profile?.fullName ? `, ${profile.fullName}` : ''}`}
                    aria-busy={loading || undefined}
                    onKeyDown={(e) => { if (e.key === 'Escape' && isOpen) { setIsOpen(false); } }}
                >
                    <img src={defaultAvatar} alt="" />
                </button>
                {isOpen && (
                    <div className={styles.menuContent} role="menu" aria-label={t('shell.menu.profileOptions')}>
                        <div className={styles.menuProfile}>
                            <img src={defaultAvatar} alt="" />
                            <div className={styles.profileDetails}>
                                <div className={styles.profileName}>{profile?.fullName || t('shell.menu.userFallback')}</div>
                                {isStaff && <StaffMenuIdentity />}
                                <button
                                    type="button"
                                    role="menuitem"
                                    className={styles.profileSettings}
                                    onClick={() => { setIsOpen(false); onSettingsClick(); }}
                                >
                                    {t('menu.accountSettings')}
                                </button>
                            </div>
                        </div>

                        {/* AB#1675 — My Business entry point. Highlighted with SET UP tag while
                            the salon owner's business setup is still incomplete. After all
                            sections are filled, the highlight goes away and the link sits as
                            a normal item. */}
                        {showMyBusinessLink && (
                            <button
                                type="button"
                                role="menuitem"
                                className={`${styles.myBusinessLink} ${isIncomplete ? styles.myBusinessHighlighted : ''}`}
                                onClick={handleMyBusinessClick}
                            >
                                <span className={styles.myBusinessIcon} aria-hidden="true">🏪</span>
                                <span className={styles.myBusinessLabel}>{t('nav.myBusiness')}</span>
                                {isIncomplete && (
                                    <span className={styles.myBusinessTag}>{t('shell.menu.setUp')}</span>
                                )}
                            </button>
                        )}

                        {/* Auth v2 profile switcher (AB#1435) — renders itself only when the
                            feature flag is on AND the user has >1 surface-able profile. */}
                        <ProfileSwitcher onClose={() => setIsOpen(false)} />

                        {/* O3 (SO-39/SO-40) — real buttons with radio semantics: one click
                            (or Enter/Space) always applies the choice. */}
                        <span className={styles.languagesTitle} id="menu-theme-label">{t('menu.theme')}</span>
                        <div className={styles.menuLanguages} role="group" aria-labelledby="menu-theme-label">
                            <button
                                type="button"
                                role="menuitemradio"
                                aria-checked={theme === 'light'}
                                className={theme === 'light' ? styles.activeLanguage : styles.inactiveLanguage}
                                onClick={handleLightTheme}
                            >
                                {t('menu.themeLight')}
                            </button>
                            <button
                                type="button"
                                role="menuitemradio"
                                aria-checked={theme === 'dark'}
                                className={theme === 'dark' ? styles.activeLanguage : styles.inactiveLanguage}
                                onClick={handleDarkTheme}
                            >
                                {t('menu.themeDark')}
                            </button>
                        </div>

                        <span className={styles.languagesTitle} id="menu-language-label">{t('menu.languages')}</span>
                        <div className={styles.menuLanguages} role="group" aria-labelledby="menu-language-label">
                            <button
                                type="button"
                                role="menuitemradio"
                                aria-checked={language === 'en'}
                                lang="en"
                                className={language === 'en' ? styles.activeLanguage : styles.inactiveLanguage}
                                onClick={handleEnglish}
                            >
                                English
                            </button>
                            <button
                                type="button"
                                role="menuitemradio"
                                aria-checked={language === 'ar'}
                                lang="ar"
                                className={language === 'ar' ? styles.activeLanguage : styles.inactiveLanguage}
                                onClick={handleArabic}
                            >
                                العربية
                            </button>
                        </div>
                        <div className={styles.menuDivider}></div>
                        <button type="button" role="menuitem" className={styles.menuItem} onClick={() => { setIsOpen(false); onFaqClick(); }}>{t('menu.termsAndConditions')}</button>
                        <button type="button" role="menuitem" className={styles.menuItem} onClick={() => { setIsOpen(false); handleLogout(); }}>{t('menu.logout')}</button>
                    </div>
                )}
            </div>
        </>
    );
};

export default Menu;
